/**
 * Chatbox Component
 *
 * Main chat component for Order Damage Cases
 * Uses PrimeReact components matching ChatWindow design pattern
 */

import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';
import moment from 'moment-timezone';
import { isEmpty, trim, get } from 'lodash';
import { withTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { ChatboxProps, Message, Participant, DamageCodeData } from './types';
import { request } from '../../utils/Request';
import { useChatListener } from './hooks/useChatListener';
import { useChatConnection } from './hooks/useChatConnection';
import Auth from '../../utils/Auth';
import { prepareChatParticipants } from './helpers/participants';
import { utcDateToLocalDate } from '../../utils/Helper';
import {
    DamageCaseFirebaseClient,
    getDamageCaseFirebaseClient,
    resolveDamageCaseFirebaseProvider,
} from '../../utils/damageCaseFirebase';
import './Chatbox.scss';

/**
 * Format timestamp to readable format (matching ChatWindow style)
 */
function formatMessageTime(timestamp: any): string {
    try {
        let dateToFormat: Date;

        // Try to parse as Firestore Timestamp first
        if (typeof timestamp === 'object' && timestamp.toDate) {
            dateToFormat = timestamp.toDate();
        } else if (typeof timestamp === 'string') {
            dateToFormat = new Date(timestamp);
        } else {
            dateToFormat = new Date(timestamp);
        }

        // Use utcDateToLocalDate to match ChatWindow format
        // @ts-ignore
        return utcDateToLocalDate(dateToFormat);
    } catch (e) {
        // Fallback to moment
        return moment(timestamp).tz(moment.tz.guess()).format('DD.M.Y HH:mm');
    }
}

/**
 * Check if message is from current user
 * Handles both prefixed (alpc_) and non-prefixed emails
 */
function isCurrentUserMessage(message: Message): boolean {
    const auth = new Auth();
    const user: any = auth.getUser();
    if (!user?.email) return false;

    const userEmail = user.email.toLowerCase();
    const messageEmail = message.email?.toLowerCase() || '';

    // Check direct match
    if (userEmail === messageEmail) return true;

    // Check if message email has alpc_ prefix and matches user email
    if (messageEmail.startsWith('alpc_')) {
        const messageEmailWithoutPrefix = messageEmail.replace(/^alpc_/, '');
        return userEmail === messageEmailWithoutPrefix;
    }

    // Check if user email should have prefix and matches
    const userEmailWithPrefix = `alpc_${userEmail}`;
    return userEmailWithPrefix === messageEmail;
}

/**
 * Sanitize HTML content to prevent XSS attacks
 * Allows safe HTML tags for formatting (like links for file attachments)
 */
function sanitizeMessage(html: string): string {
    if (!html) return '';

    // Configure DOMPurify to allow safe HTML
    // This allows basic formatting and links (useful for file attachments)
    const config = {
        ALLOWED_TAGS: ['a', 'b', 'i', 'em', 'strong', 'p', 'br', 'span', 'div'],
        ALLOWED_ATTR: ['href', 'target', 'rel'],
        ALLOW_DATA_ATTR: false,
    };

    return DOMPurify.sanitize(html, config);
}

/**
 * Check if current user is a participant in the chat
 */
function isUserParticipant(
    userEmail: string | null | undefined,
    participants: Participant[]
): boolean {
    if (!userEmail) return false;

    const userEmailLower = userEmail.toLowerCase();
    return participants.some(
        p => p.email?.toLowerCase() === userEmailLower
    );
}

const Chatbox: React.FC<ChatboxProps & { t?: any }> = ({
    isOpen,
    onClose,
    damageCase,
    participants: initialParticipants = [],
    masterUser,
    allDamageCodes = [],
    onChangeDamageCode,
    t,
}) => {
    const [message, setMessage] = useState<string>('');
    const [isSending, setIsSending] = useState<boolean>(false);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [isAccepting, setIsAccepting] = useState<boolean>(false);
    const [firebaseClient, setFirebaseClient] = useState<DamageCaseFirebaseClient | null>(null);
    const chatWindowRef = useRef<any>(null);
    const fileInputRef = useRef<any>(null);
    const auth = new Auth();
    const chatToken = damageCase?.emil_frey_id ? String(damageCase.emil_frey_id) : null;
    const firebaseProvider = React.useMemo(
        () => resolveDamageCaseFirebaseProvider(damageCase?.source_type),
        [damageCase?.source_type]
    );

    useEffect(() => {
        if (!isOpen || !chatToken) {
            setFirebaseClient(null);
            return;
        }

        setFirebaseClient(getDamageCaseFirebaseClient(firebaseProvider));
    }, [isOpen, chatToken, firebaseProvider]);
    const isFirebaseClientReady = firebaseClient?.provider === firebaseProvider;

    // Get chat data from Firestore
    const { messages, participants: firestoreParticipants, loading } = useChatListener({
        chatToken,
        firebaseClient,
        enabled: isOpen && isFirebaseClientReady,
    });

    // Debug log incoming participants prop
    // useEffect(() => {
    //     // eslint-disable-next-line no-console
    //     console.log('[Chatbox] incoming participants prop', initialParticipants);
    // }, [initialParticipants]);

    // Prepare final participants list (merge all sources)
    const finalParticipants = React.useMemo(() => {
        if (!damageCase) return initialParticipants;
        return prepareChatParticipants(
            damageCase,
            firestoreParticipants,
            masterUser,
            initialParticipants
        );
    }, [damageCase, firestoreParticipants, initialParticipants, masterUser]);

    // For connection initialization, prefer the raw participants prop if provided
    // (to ensure all backend participants are pushed to Firestore). Fallback to merged list.
    const connectionParticipants = React.useMemo(() => {
        if (initialParticipants && initialParticipants.length > 0) {
            return initialParticipants;
        }
        return finalParticipants;
    }, [initialParticipants, finalParticipants]);

    // Initialize chat connection
    useChatConnection({
        chatToken,
        participants: connectionParticipants,
        firebaseClient,
        enabled: isOpen && isFirebaseClientReady,
    });

    // Scroll to bottom when new messages arrive
    useEffect(() => {
        if (chatWindowRef.current && messages.length > 0) {
            setTimeout(() => {
                if (chatWindowRef.current) {
                    chatWindowRef.current.scrollTo({
                        top: chatWindowRef.current.scrollHeight,
                        behavior: 'smooth'
                    });
                }
            }, 100);
        }
    }, [messages]);

    // Reset message input when chat closes
    useEffect(() => {
        if (!isOpen) {
            setMessage('');
        }
    }, [isOpen]);

    /**
     * Send message to Firestore
     */
    const sendMessage = async () => {
        if (isEmpty(trim(message)) || isSending || !chatToken || !firebaseClient || !isFirebaseClientReady) {
            return;
        }

        // Get current user
        const user: any = auth.getUser();
        let senderEmail = user?.email || '';

        // Add alpc_ prefix to email if it doesn't already have it
        if (senderEmail && !senderEmail.startsWith('alpc_')) {
            senderEmail = `alpc_${senderEmail}`;
        }

        // If user not in participants, allow send but warn (backend/state may be lagging)
        if (!isUserParticipant(senderEmail, finalParticipants)) {
            console.warn('User not found in participants; proceeding to send to avoid blocking chat.');
        }

        setIsSending(true);
        const messageText = message;
        setMessage(''); // Clear input immediately for better UX

        try {
            const senderLabel = user?.name ||
                              (user?.first_name && user?.last_name
                                 ? `${user.first_name} ${user.last_name}`
                                 : user?.email || 'Unknown');

            // SECURITY: Sanitize message content before storing
            const sanitizedMessage = sanitizeMessage(messageText);

            // Generate unique message ID
            const uniqueId = `${Date.now()}${Math.floor(Math.random() * 10000)}`;

            const newMessage: Message = {
                id: uniqueId,
                message: sanitizedMessage, // Store sanitized message
                time: new Date().toISOString(),
                type: 0,
                is_file: 0,
                name: senderLabel,  // Message interface still uses 'name' field
                email: senderEmail, // Email with alpc_ prefix
            };

            // Update Firestore
            const chatRef = firebaseClient.firestore.collection('chats').doc(chatToken);
            await chatRef.update({
                messages: firebaseClient.firebase.firestore.FieldValue.arrayUnion(newMessage),
            });

            if (damageCase?.id) {
                request({
                    method: 'POST',
                    url: `/suppliers/provider/order/damage/cases/${damageCase.id}/chat/comment`,
                    data: {
                        damage_code_id: chatToken,
                        comments: sanitizedMessage,
                    },
                }).catch(error => {
                    console.error('Error logging chat comment:', error);
                });
            }

            // Scroll to bottom after sending
            setTimeout(() => {
                if (chatWindowRef.current) {
                    chatWindowRef.current.scrollTo({
                        top: chatWindowRef.current.scrollHeight,
                        behavior: 'smooth'
                    });
                }
            }, 100);

        } catch (error) {
            console.error('Error sending message:', error);
            // Restore message on error
            setMessage(messageText);
        } finally {
            setIsSending(false);
        }
    };

    /**
     * Handle Enter key press in textarea (Enter = submit, Shift+Enter = new line)
     */
    const handleInputKeyDown = (e: any) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    /**
     * Validate file before upload
     */
    const validateFile = (file: File): string | null => {
        // Maximum file size: 10MB
        const maxSize = 10 * 1024 * 1024; // 10MB in bytes
        if (file.size > maxSize) {
            return t('file size must be less than 10MB');
        }

        // Allowed file types
        const allowedTypes = [
            // Images
            'image/jpeg',
            'image/jpg',
            'image/png',
            'image/gif',
            'image/webp',
            'image/bmp',
            'image/svg+xml',
            // PDF
            'application/pdf',
            // Word documents
            'application/msword', // .doc
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
            // Excel documents
            'application/vnd.ms-excel', // .xls
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        ];

        // Also check file extension as fallback (for some browsers)
        const fileName = file.name.toLowerCase();
        const allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg', '.pdf', '.doc', '.docx', '.xls', '.xlsx'];
        const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

        if (!allowedTypes.includes(file.type) && !hasValidExtension) {
            return t('only images, pdf, word, and excel files are allowed');
        }

        return null;
    };

    /**
     * Handle file upload
     */
    const handleFileUpload = async (event: any) => {
        const file = event.target.files?.[0];
        if (!file || !chatToken || !firebaseClient || !isFirebaseClientReady) {
            return;
        }

        // Validate file before uploading
        const validationError = validateFile(file);
        if (validationError) {
            alert(validationError);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            return;
        }

        setIsUploading(true);

        try {
            // Prepare form data
            const formData = new FormData();
            formData.append('file', file, file.name);
            formData.append('upload_alp', '1');

            // Upload file
            const response = await request({
                url: '/upload',
                method: 'POST',
                headers: { 'content-type': 'multipart/form-data' },
                data: formData,
            });

            const uploadData = response.data?.data;
            if (!uploadData) {
                throw new Error('Invalid upload response');
            }

            // Get alp_url from response
            const alpUrl = uploadData.alp_url;
            const fileName = uploadData.name || file.name;

            if (!alpUrl) {
                throw new Error('Invalid upload response');
            }

            // Get current user
            const user: any = auth.getUser();
            let senderEmail = user?.email || '';

            // Add alpc_ prefix to email if it doesn't already have it
            if (senderEmail && !senderEmail.startsWith('alpc_')) {
                senderEmail = `alpc_${senderEmail}`;
            }

            const senderLabel = user?.name ||
                (user?.first_name && user?.last_name
                    ? `${user.first_name} ${user.last_name}`
                    : user?.email || 'Unknown');

            // Generate unique message ID
            const uniqueId = `${Date.now()}${Math.floor(Math.random() * 10000)}`;

            // Create file message with HTML link
            const fileLinkMessage = `<a href="${alpUrl}" target="_blank">${fileName}</a>`;

            const newMessage: Message = {
                id: uniqueId,
                message: fileLinkMessage,
                time: new Date().toISOString(),
                type: 0,
                is_file: 1,
                name: senderLabel,
                email: senderEmail,
            };

            // Update Firestore
            const chatRef = firebaseClient.firestore.collection('chats').doc(chatToken);
            await chatRef.update({
                messages: firebaseClient.firebase.firestore.FieldValue.arrayUnion(newMessage),
            });

            // Scroll to bottom after sending
            setTimeout(() => {
                if (chatWindowRef.current) {
                    chatWindowRef.current.scrollTo({
                        top: chatWindowRef.current.scrollHeight,
                        behavior: 'smooth'
                    });
                }
            }, 100);

        } catch (error) {
            console.error('Error uploading file:', error);
        } finally {
            setIsUploading(false);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    /**
     * Trigger file input click
     */
    const triggerFileUpload = () => {
        fileInputRef.current?.click();
    };

    // Get damage code display text (description preferred, fallback to code)
    const damageCodeDisplay = (damageCase?.damage_code + " " + damageCase?.damage_code_description) || '';

    // Filter rejected damage codes for dropdown
    const rejectedDamageCodes = React.useMemo(() => {
        if (!allDamageCodes || allDamageCodes.length === 0) return [];
        return allDamageCodes.filter((code: DamageCodeData) => code.was_rejected === true);
    }, [allDamageCodes]);

    // Get current selected damage code emil_frey_id for dropdown value
    const currentDamageCodeId = damageCase?.emil_frey_id ? String(damageCase.emil_frey_id) : '';

    // Get current damage code object from allDamageCodes
    const currentDamageCode = React.useMemo(() => {
        if (!allDamageCodes || allDamageCodes.length === 0 || !currentDamageCodeId) return null;
        return allDamageCodes.find(
            (code: DamageCodeData) => String(code.emil_frey_id) === currentDamageCodeId
        ) || null;
    }, [allDamageCodes, currentDamageCodeId]);

    // Check if current damage code is editable (only show accept button if editable)
    const isCurrentCodeEditable = (currentDamageCode?.is_editable === true && currentDamageCode?.carrier_status === "rejected");

    // Handle damage code change from dropdown
    const handleDamageCodeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedCodeId = event.target.value;
        if (!selectedCodeId || !onChangeDamageCode) return;

        // Find the selected code by emil_frey_id (since that's what's used for chat token)
        const selectedCode = rejectedDamageCodes.find(
            (code: DamageCodeData) => String(code.emil_frey_id) === selectedCodeId
        );

        if (selectedCode && onChangeDamageCode) {
            onChangeDamageCode(selectedCode);
        }
    };

    /**
     * Handle accept damage code
     */
    const onAcceptDamageCode = async () => {
        if (!currentDamageCode || !damageCase?.id || !currentDamageCode.emil_frey_id) {
            return;
        }
        setIsAccepting(true);

        try {
            await request({
                method: 'POST',
                url: `/suppliers/provider/order/damage/cases/${get(damageCase, ['id'])}/update/damage/codes`,
                data: {
                    comments: 'Accepted from chat',
                    close_label: false,
                    close_all_labels: false,
                    send_notification_efl: false,
                    send_notification_carrier: false,
                    send_notification_compound: false,
                    send_notification_dekra: false,
                    type: "code-status-update",
                    damage_codes: [{
                        id: currentDamageCode.emil_frey_id,
                        carrier_status: 'accepted'
                    }],
                },
            });

            // Success - close the chatbox
            onClose();
        } catch (error) {
            console.error('Error accepting damage code:', error);
        } finally {
            setIsAccepting(false);
        }
    };

    /**
     * Show confirmation and accept damage code
     */
    const confirmAcceptDamageCode = (event: any) => {
        confirmPopup({
            target: event.currentTarget,
            message: t('Do you want to accept this damage code?'),
            icon: 'pi pi-info-circle',
            accept: () => onAcceptDamageCode(),
            reject: () => {
                // User cancelled
            },
        });
    };

    // Dialog header (matching ELP format: "Damage Case {caseNumber} Damage Code: {description or code}")
    const header = (
        <>
            <div className="flex align-items-center justify-content-between">
                <div className="relative flex align-items-center mr-3">
                    {damageCase?.case_number && (
                        <>
                            <span className="text-900 font-semibold mr-2">
                                #{damageCase.case_number}
                            </span>
                            {damageCodeDisplay && (
                                <span className="text-700">
                                    {' '}{damageCodeDisplay}
                                </span>
                            )}
                        </>
                    )}
                    {!damageCase?.case_number && damageCodeDisplay && (
                        <span className="text-700">
                            {damageCodeDisplay}
                        </span>
                    )}
                </div>
                {rejectedDamageCodes.length > 0 && onChangeDamageCode && (
                    <div className="flex align-items-center ml-3">
                        <select
                            className="p-inputtext p-component"
                            onChange={handleDamageCodeChange}
                            value={currentDamageCodeId}
                            style={{
                                padding: '0.25rem 0.5rem',
                                fontSize: '0.875rem',
                                border: '1px solid #ced4da',
                                borderRadius: '4px',
                            }}
                        >
                            {rejectedDamageCodes.map((code: DamageCodeData) => (
                                <option
                                    key={code.emil_frey_id || code.id}
                                    value={String(code.emil_frey_id)}
                                >
                                    {code.code} - {code.description || ''}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </div>
            <Divider className="mb-0" />
        </>
    );

    return (
        <Dialog
            visible={isOpen}
            onHide={onClose}
            keepInViewport={true}
            className="chat-window damage-case-chatbox"
            draggable={true}
            modal={false}
            closeOnEscape={false}
            position="bottom-right"
            resizable={true}
            header={header}
        >
            <div className="flex flex-column h-full">
                <div style={{ position: 'absolute', bottom: '-2px', right: '2px' }}>
                    <i className="pi pi-arrow-down-right"></i>
                </div>

                {/* Messages Area */}
                <div
                    ref={chatWindowRef}
                    className="p-0 md:px-4 lg:px-6 lg:py-4 mt-2 overflow-y-auto"
                    style={{ height: '40vh' }}
                >
                    {loading && messages.length === 0 ? (
                        <div className="text-center text-700">
                            {t?.('loading') || 'Loading...'}
                        </div>
                    ) : messages.length === 0 ? (
                        <div className="text-center text-700">
                            {t?.('no messages') || 'No messages yet. Start the conversation!'}
                        </div>
                    ) : (
                        messages.map((msg: Message) => {
                            const isOwnMessage = isCurrentUserMessage(msg);

                            return (
                                <div key={msg.id}>
                                    {isOwnMessage ? (
                                        <div className="grid grid-nogutter mb-2">
                                            <div className="col text-right">
                                                {msg.name && (
                                                    <p className="text-900 font-semibold mb-1">
                                                        <span>{msg.name}</span>
                                                    </p>
                                                )}
                                                <span
                                                    className="inline-block text-left font-medium border-1 surface-border bg-primary-100 text-primary-900 p-2 white-space-normal border-round text-sm"
                                                    style={{
                                                        wordBreak: "break-word",
                                                        maxWidth: "80%",
                                                    }}
                                                >
                                                    <div dangerouslySetInnerHTML={{ __html: sanitizeMessage(msg.message) }} />
                                                </span>
                                                <p className="text-700 mt-1 text-sm">
                                                    {formatMessageTime(msg.time)}{' '}
                                                    <i className="pi pi-check ml-2 text-green-400"></i>
                                                </p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="grid grid-nogutter mb-2">
                                            <div className="col">
                                                {msg.name && (
                                                    <p className="text-900 font-semibold mb-1">
                                                        <span>{msg.name}</span>
                                                    </p>
                                                )}
                                                <span
                                                    className="text-700 inline-block font-medium border-1 surface-border p-2 white-space-normal border-round text-sm"
                                                    style={{
                                                        wordBreak: "break-word",
                                                        maxWidth: "80%",
                                                    }}
                                                >
                                                    <div dangerouslySetInnerHTML={{ __html: sanitizeMessage(msg.message) }} />
                                                </span>
                                                <p className="text-700 mt-1 text-sm">
                                                    {formatMessageTime(msg.time)}
                                                    <i className="pi pi-check ml-2 text-green-400"></i>
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Input Area */}
                <div className="md:p-2 sm:p-2 pb-0 mt-auto border-top-1 surface-border">
                    <InputTextarea
                        id="message"
                        placeholder={t('type message here')}
                        className="w-full border-round"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyDown={handleInputKeyDown}
                        disabled={isSending || isUploading || loading || !isFirebaseClientReady}
                        rows={3}
                        autoResize
                    />
                    <div className="flex gap-3 mt-3">
                        <Button
                            onClick={sendMessage}
                            label={t('send')}
                            icon="pi pi-send"
                            disabled={isEmpty(trim(message)) || isSending || isUploading || loading || !isFirebaseClientReady}
                            loading={isSending}
                            size={"small"}
                        />
                        <Button
                            onClick={triggerFileUpload}
                            icon="pi pi-paperclip"
                            label={t('upload file')}
                            disabled={isSending || isUploading || loading || !isFirebaseClientReady}
                            loading={isUploading}
                            outlined
                            severity="secondary"
                            tooltipOptions={{ position: 'top' }}
                            size={"small"}
                        />
                        {isCurrentCodeEditable && (
                            <Button
                                onClick={confirmAcceptDamageCode}
                                label={t('accept')}
                                icon="pi pi-check"
                                size="small"
                                severity="success"
                                loading={isAccepting}
                                disabled={isAccepting || isSending || isUploading || loading || !isFirebaseClientReady}
                            />
                        )}
                    </div>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                    />
                </div>
            </div>
            <ConfirmPopup />
        </Dialog>
    );
};

export default withTranslation()(Chatbox);

